import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { submitDsaForm, resetDsaState } from "../store/slices/dsaSlice";
import "./Dsa.css"; // ✅ Re-enabled for proper styling

export default function Dsa({ isOpen, onClose }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Modal ref for scroll handling
  const modalRef = React.useRef(null);

  // Redux State
  const { loading, error, success } = useSelector((state) => state.dsa);

  const [dsaPopupType, setDsaPopupType] = useState("");
  const [dsaPopupMessage, setDsaPopupMessage] = useState("");
  const [dsaStep, setDsaStep] = useState(1);
  const [showDsaPopup, setShowDsaPopup] = useState(false);
  const [selfiePhoto, setSelfiePhoto] = useState(null);
  const [aadhaarFront, setAadhaarFront] = useState(null);
  const [aadhaarBack, setAadhaarBack] = useState(null);
  const [panCardPhoto, setPanCardPhoto] = useState(null);
  const [cancelCheque, setCancelCheque] = useState(null);
  const [officePhoto, setOfficePhoto] = useState(null);
  const [otherDocuments, setOtherDocuments] = useState([]);
  const [gender, setGender] = useState("");
  const [selfiePreviewUrl, setSelfiePreviewUrl] = useState("");
  const [officePreviewUrl, setOfficePreviewUrl] = useState("");
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const [dsaForm, setDsaForm] = useState({
    fullname: "",
    pan: "",
    dob: "",
    email: "",
    mobile: "",
    gender: "",
    aadhaar_number: "",
    address: "",
    pincode: "",
    city: "",
    state: "",
    area: "",
    experience: "",
    profession: "",
  });

  // Validation errors
  const [errors, setErrors] = useState({});

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      resetDsaForm();
      dispatch(resetDsaState());
      // ✅ BLOCK BACKGROUND SCROLL
      document.body.style.overflow = 'hidden';
    } else {
      // ✅ RESTORE BACKGROUND SCROLL
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, dispatch]);

  // ✅ FORCE MOUSE WHEEL SCROLL & BLOCK BACKGROUND
  useEffect(() => {
    if (!isOpen) return;
    
    const modalElement = modalRef.current;
    const overlayElement = modalElement?.parentElement;
    
    if (!modalElement || !overlayElement) return;

    // Block wheel on overlay (background)
    const blockOverlayWheel = (e) => {
      if (e.target === overlayElement) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Allow wheel on modal
    const handleModalWheel = (e) => {
      e.stopPropagation();
    };

    overlayElement.addEventListener('wheel', blockOverlayWheel, { passive: false });
    modalElement.addEventListener('wheel', handleModalWheel, { passive: false });

    return () => {
      overlayElement.removeEventListener('wheel', blockOverlayWheel);
      modalElement.removeEventListener('wheel', handleModalWheel);
    };
  }, [isOpen]);

  // Handle Saga Response
  useEffect(() => {
    if (success) {
      showNotification(
        "DSA Form Submitted Successfully! Thank you for choosing Krishna Finconcept Services!",
        "success",
      );
      setTimeout(() => {
        resetDsaForm();
        dispatch(resetDsaState());
        onClose();
      }, 3000);
    }
    if (error) {
      // ✅ SHOW BACKEND VALIDATION ERRORS - Both in fields AND toast
      // Check if error is an object with multiple field errors
      if (typeof error === "object" && error !== null) {
        const fieldErrors = {};
        const errorMessages = [];
        
        Object.keys(error).forEach((key) => {
          const message = error[key];
          // Handle array of errors (backend sends arrays)
          const errorText = Array.isArray(message) ? message[0] : message;
          
          // Map backend field names to frontend field names
          const fieldMapping = {
            'email': 'email',
            'mobile_number': 'mobile',
            'pan': 'pan',
            'aadhaar_number': 'aadhaar_number',
            'fullname': 'fullname',
            'dob': 'dob',
            'pincode': 'pincode',
            'address': 'address'
          };
          
          const frontendField = fieldMapping[key] || key;
          fieldErrors[frontendField] = errorText;
          errorMessages.push(errorText);
        });
        
        // Set field-specific errors
        setErrors((prev) => ({ ...prev, ...fieldErrors }));
        
        // Show toast with all error messages
        if (errorMessages.length > 0) {
          showNotification(
            errorMessages.join('\n'),
            "error"
          );
        }
      }
      // Handle string errors
      else if (typeof error === "string") {
        showNotification(error, "error");
      } 
      // Handle array errors
      else if (Array.isArray(error)) {
        showNotification(error.join('\n'), "error");
      } 
      // Generic error
      else {
        showNotification("An error occurred. Please try again.", "error");
      }
    }
  }, [success, error, dispatch, onClose]);

  // ---------- Handlers for form fields ----------
  const handleGenderChange = (value) => {
    setDsaForm((prev) => ({ ...prev, gender: value }));
    setGender(value);
    setErrors((prev) => ({ ...prev, gender: null }));
  };

  const handleDsaInputChange = (field, value) => {
    setDsaForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const handlePincodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    handleDsaInputChange("pincode", value);

    if (value.length < 6) {
      setDsaForm((prev) => ({
        ...prev,
        city: "",
        state: "",
      }));
    }
  };

  const handlePincodeBlur = async (e) => {
    const pincode = e.target.value.trim();
    if (pincode.length === 6) {
      await fetchLocationFromPincode(pincode);
    }
  };

  const fetchLocationFromPincode = async (pincode) => {
    if (!pincode || pincode.length !== 6) return;
    setIsLoadingLocation(true);
    try {
      const response = await fetch(
        `https://api.postalpincode.in/pincode/${pincode}`,
      );
      const data = await response.json();
      if (data[0].Status === "Success") {
        const postOffice = data[0].PostOffice[0];
        setDsaForm((prev) => ({
          ...prev,
          city: postOffice.District || postOffice.Name,
          state: postOffice.State,
          area: postOffice.Name,
        }));
        setErrors((prev) => ({ ...prev, pincode: null }));
      } else {
        setErrors((prev) => ({
          ...prev,
          pincode: "Pincode not found. Please enter manually.",
        }));
      }
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        pincode: "Unable to fetch location. Please enter manually.",
      }));
    } finally {
      setIsLoadingLocation(false);
    }
  };

  // ---------- File handling with camera & gallery ----------
  const handleFileUpload = (
    setter,
    file,
    maxSizeMB = 5,
    isImageOnly = false,
  ) => {
    if (!file) return;

    // Size validation
    const maxSize = maxSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      // ✅ NO TOAST - Set error in state instead
      console.error(`File size must be less than ${maxSizeMB}MB`);
      return;
    }

    // Type validation
    const allowedTypes = isImageOnly
      ? ["image/jpeg", "image/jpg", "image/png"]
      : ["image/jpeg", "image/jpg", "image/png", "application/pdf"];

    if (!allowedTypes.includes(file.type)) {
      // ✅ NO TOAST - Set error in state instead
      console.error(
        isImageOnly
          ? "Please upload only JPG or PNG images"
          : "Please upload only JPG, PNG, or PDF files"
      );
      return;
    }

    setter(file);

    // Generate preview for images
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (setter === setSelfiePhoto) {
          setSelfiePreviewUrl(e.target.result);
        } else if (setter === setOfficePhoto) {
          setOfficePreviewUrl(e.target.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Helper to trigger file input
  const triggerFileInput = (inputId) => {
    document.getElementById(inputId)?.click();
  };

  const addOtherDocumentField = () => {
    setOtherDocuments([
      ...otherDocuments,
      {
        id: Date.now(),
        text: "",
        file: null,
        name: "",
        size: 0,
        type: "",
      },
    ]);
  };

  const removeOtherDocumentField = (id) => {
    setOtherDocuments(otherDocuments.filter((doc) => doc.id !== id));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0 || !bytes) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  // ---------- Navigation & Validation (with detailed popup) ----------
  const validateCurrentStep = () => {
    const newErrors = {};
    const errorMessages = [];

    switch (dsaStep) {
      case 1:
        if (!dsaForm.fullname) {
          newErrors.fullname = "Full name is required";
          errorMessages.push("Full name is required");
        }
        if (!dsaForm.pan) {
          newErrors.pan = "PAN number is required";
          errorMessages.push("PAN number is required");
        } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(dsaForm.pan)) {
          newErrors.pan = "Invalid PAN format (e.g., ABCDE1234F)";
          errorMessages.push("Invalid PAN format");
        }
        if (!dsaForm.dob) {
          newErrors.dob = "Date of birth is required";
          errorMessages.push("Date of birth is required");
        }
        if (!dsaForm.email) {
          newErrors.email = "Email is required";
          errorMessages.push("Email is required");
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(dsaForm.email)) {
          newErrors.email = "Invalid email format";
          errorMessages.push("Invalid email format");
        }
        if (!dsaForm.mobile) {
          newErrors.mobile = "Mobile number is required";
          errorMessages.push("Mobile number is required");
        } else if (!/^\d{10}$/.test(dsaForm.mobile)) {
          newErrors.mobile = "Mobile must be 10 digits";
          errorMessages.push("Mobile must be 10 digits");
        }
        if (!dsaForm.gender) {
          newErrors.gender = "Gender is required";
          errorMessages.push("Gender is required");
        }
        if (!dsaForm.aadhaar_number) {
          newErrors.aadhaar_number = "Aadhaar number is required";
          errorMessages.push("Aadhaar number is required");
        } else if (!/^\d{12}$/.test(dsaForm.aadhaar_number)) {
          newErrors.aadhaar_number = "Aadhaar must be 12 digits";
          errorMessages.push("Aadhaar must be 12 digits");
        } else if (!/^[2-9]/.test(dsaForm.aadhaar_number)) {
          newErrors.aadhaar_number = "Aadhaar number must start with 2-9";
          errorMessages.push("Aadhaar number must start with 2-9");
        }
        if (!dsaForm.address) {
          newErrors.address = "Address is required";
          errorMessages.push("Address is required");
        }
        if (!dsaForm.pincode) {
          newErrors.pincode = "Pincode is required";
          errorMessages.push("Pincode is required");
        } else if (!/^\d{6}$/.test(dsaForm.pincode)) {
          newErrors.pincode = "Pincode must be 6 digits";
          errorMessages.push("Pincode must be 6 digits");
        }
        break;

      case 2:
        if (!dsaForm.experience) {
          newErrors.experience = "Experience is required";
          errorMessages.push("Experience is required");
        }
        if (!dsaForm.profession) {
          newErrors.profession = "Profession is required";
          errorMessages.push("Profession is required");
        }
        if (!selfiePhoto) {
          newErrors.selfie = "Selfie photo is required";
          errorMessages.push("Selfie photo is required");
        }
        break;

      case 3:
        if (!aadhaarFront) {
          newErrors.aadhaarFront = "Aadhaar front is required";
          errorMessages.push("Aadhaar front is required");
        }
        if (!aadhaarBack) {
          newErrors.aadhaarBack = "Aadhaar back is required";
          errorMessages.push("Aadhaar back is required");
        }
        if (!panCardPhoto) {
          newErrors.panCard = "PAN card is required";
          errorMessages.push("PAN card is required");
        }
        if (!cancelCheque) {
          newErrors.cancelCheque = "Cancel cheque is required";
          errorMessages.push("Cancel cheque is required");
        }
        if (!termsAccepted) {
          newErrors.terms = "You must accept terms & conditions";
          errorMessages.push("You must accept terms & conditions");
        }
        break;
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      // ✅ Scroll to top to show validation errors
      if (modalRef.current) {
        modalRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return false;
    }
    return true;
  };

  const goToNextStep = () => {
    if (validateCurrentStep()) {
      setDsaStep((prev) => prev + 1);
      // ✅ Scroll modal to top, not window
      if (modalRef.current) {
        modalRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const goToPrevStep = () => {
    setDsaStep((prev) => prev - 1);
    // ✅ Scroll modal to top, not window
    if (modalRef.current) {
      modalRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // ---------- Final Submission using Redux Saga ----------
  const handleDsaSubmit = (e) => {
    e.preventDefault();

    // Validate final step before submission
    if (!validateCurrentStep()) {
      return;
    }

    const formData = new FormData();

    // Step 1: Personal details
    formData.append("full_name", dsaForm.fullname);
    formData.append("pan_number", dsaForm.pan);
    formData.append("date_of_birth", dsaForm.dob);
    formData.append("email", dsaForm.email);
    formData.append("mobile_number", dsaForm.mobile);
    formData.append("gender", dsaForm.gender.toLowerCase());
    formData.append("aadhar_number", dsaForm.aadhaar_number);
    formData.append("address", dsaForm.address);
    formData.append("pincode", dsaForm.pincode);
    formData.append("city", dsaForm.city);
    formData.append("area_locality", dsaForm.area);
    formData.append("state", dsaForm.state);

    // Step 2: Professional details & photos
    formData.append("experience_years", dsaForm.experience);
    formData.append("current_profession", dsaForm.profession);
    if (selfiePhoto) formData.append("selfie_photo", selfiePhoto);
    if (officePhoto) formData.append("office_photo", officePhoto);

    // Step 3: Documents & terms
    formData.append("terms_accepted", termsAccepted ? "1" : "0");
    if (aadhaarFront) formData.append("aadhar_card_front", aadhaarFront);
    if (aadhaarBack) formData.append("aadhar_card_back", aadhaarBack);
    if (panCardPhoto) formData.append("pan_card", panCardPhoto);
    if (cancelCheque) formData.append("cancel_cheque", cancelCheque);

    // Other documents (if any)
    otherDocuments.forEach((doc, index) => {
      if (doc && doc.file) {
        formData.append(`other_documents[${index}][file]`, doc.file);
        formData.append(
          `other_documents[${index}][document_type]`,
          doc.type || "other",
        );
        formData.append(
          `other_documents[${index}][document_name]`,
          doc.name || `Document ${index + 1}`,
        );
      }
    });

    // Dispatch action to submit form via Redux Saga
    dispatch(submitDsaForm(formData));
  };

  // ---------- Reset ----------
  const resetDsaForm = () => {
    localStorage.removeItem("dsa_user_id");
    setDsaForm({
      fullname: "",
      pan: "",
      dob: "",
      email: "",
      mobile: "",
      gender: "",
      aadhaar_number: "",
      address: "",
      pincode: "",
      city: "",
      state: "",
      area: "",
      experience: "",
      profession: "",
    });
    setGender("");
    setOtherDocuments([]);
    setAadhaarFront(null);
    setAadhaarBack(null);
    setPanCardPhoto(null);
    setCancelCheque(null);
    setOfficePhoto(null);
    setSelfiePhoto(null);
    setSelfiePreviewUrl("");
    setOfficePreviewUrl("");
    setTermsAccepted(false);
    setDsaStep(1);
    setErrors({});
  };

  // ---------- Notification ----------
  const showNotification = (message, type) => {
    setDsaPopupMessage(message);
    setDsaPopupType(type);
    setShowDsaPopup(true);
    setTimeout(() => setShowDsaPopup(false), 5000);
  };

  // ---------- Render ----------
  if (!isOpen) return null;

  return (
    <>
      <div className="dsa-modal-overlay" onClick={(e) => {
        // Only close if clicking on overlay, not on modal
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}>
        <div className="dsa-modal" ref={modalRef}>{/* Added ref for scroll */}
          <div className="dsa-modal-header">
            <div className="modal-header-content">
              <div className="modal-title-icon">
                <i className="fas fa-handshake"></i>
              </div>
              <div>
                <h2>Become a DSA Partner</h2>
                <p>Join India's Fastest Growing Loan Distribution Network</p>
              </div>
            </div>
            <button className="close-modal-btn" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>

          <div className="modal-progress-steps">
            {[
              { num: 1, text: "Personal Details", icon: "user" },
              { num: 2, text: "Professional", icon: "briefcase" },
              { num: 3, text: "Documents", icon: "file-alt" },
            ].map((step) => (
              <div
                key={step.num}
                className={`progress-step ${dsaStep >= step.num ? "active" : ""}`}
              >
                <span className="step-number">
                  <i className={`fas fa-${step.icon}`}></i>
                </span>
                <span className="step-text">{step.text}</span>
              </div>
            ))}
          </div>

          <div className="dsa-modal-content">
            <form onSubmit={handleDsaSubmit}>
              {/* Popup with multiline support */}
              {showDsaPopup && (
                <div className={`dsa-popup ${dsaPopupType}`}>
                  <i
                    className={`fas ${dsaPopupType === "success" ? "fa-check-circle" : "fa-exclamation-circle"}`}
                  ></i>
                  <p style={{ whiteSpace: "pre-line" }}>{dsaPopupMessage}</p>
                </div>
              )}

              {/* Step 1: Personal Information */}
              {dsaStep === 1 && (
                <div className="form-step">
                  <div className="form-section-header">
                    <h3>
                      <i className="fas fa-user-circle"></i>
                      Personal Information
                    </h3>
                    <p>
                      Fill in your basic details to begin the partnership
                      process
                    </p>
                  </div>

                  <div className="form-grid">
                    <div className="form-group">
                      <label>Full Name (as per PAN Card) *</label>
                      <div
                        className={`input-wrapper ${focusedField === "fullname" ? "focused" : ""} ${errors.fullname ? "error" : ""}`}
                      >
                        <i className="fas fa-user input-icon"></i>
                        <input
                          type="text"
                          placeholder="eg. Rakesh Kumar Sharma"
                          value={dsaForm.fullname}
                          onChange={(e) =>
                            handleDsaInputChange("fullname", e.target.value)
                          }
                          onFocus={() => setFocusedField("fullname")}
                          onBlur={() => setFocusedField(null)}
                        />
                      </div>
                      {errors.fullname && (
                        <div className="error-message">
                          <i className="fas fa-exclamation-circle"></i>
                          {errors.fullname}
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label>PAN Number *</label>
                      <div
                        className={`input-wrapper ${focusedField === "pan" ? "focused" : ""} ${errors.pan ? "error" : ""}`}
                      >
                        <i className="fas fa-id-card input-icon"></i>
                        <input
                          type="text"
                          placeholder="eg. ABCDE1234F"
                          value={dsaForm.pan}
                          onChange={(e) =>
                            handleDsaInputChange(
                              "pan",
                              e.target.value.toUpperCase(),
                            )
                          }
                          onFocus={() => setFocusedField("pan")}
                          onBlur={() => setFocusedField(null)}
                          maxLength="10"
                        />
                      </div>
                      {errors.pan && (
                        <div className="error-message">
                          <i className="fas fa-exclamation-circle"></i>
                          {errors.pan}
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Date of Birth *</label>
                      <div
                        className={`input-wrapper ${focusedField === "dob" ? "focused" : ""} ${errors.dob ? "error" : ""}`}
                      >
                        <i className="fas fa-calendar input-icon"></i>
                        <input
                          type="date"
                          value={dsaForm.dob}
                          onChange={(e) =>
                            handleDsaInputChange("dob", e.target.value)
                          }
                          onFocus={() => setFocusedField("dob")}
                          onBlur={() => setFocusedField(null)}
                        />
                      </div>
                      {errors.dob && (
                        <div className="error-message">
                          <i className="fas fa-exclamation-circle"></i>
                          {errors.dob}
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Email *</label>
                      <div
                        className={`input-wrapper ${focusedField === "email" ? "focused" : ""} ${errors.email ? "error" : ""}`}
                      >
                        <i className="fas fa-envelope input-icon"></i>
                        <input
                          type="email"
                          placeholder="eg. rakesh@example.com"
                          value={dsaForm.email}
                          onChange={(e) =>
                            handleDsaInputChange("email", e.target.value)
                          }
                          onFocus={() => setFocusedField("email")}
                          onBlur={() => setFocusedField(null)}
                        />
                      </div>
                      {errors.email && (
                        <div className="error-message">
                          <i className="fas fa-exclamation-circle"></i>
                          {errors.email}
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Mobile *</label>
                      <div
                        className={`input-wrapper ${focusedField === "mobile" ? "focused" : ""} ${errors.mobile ? "error" : ""}`}
                      >
                        <i className="fas fa-phone input-icon"></i>
                        <input
                          type="tel"
                          placeholder="eg. 9737304440"
                          value={dsaForm.mobile}
                          onChange={(e) =>
                            handleDsaInputChange(
                              "mobile",
                              e.target.value.replace(/\D/g, ""),
                            )
                          }
                          onFocus={() => setFocusedField("mobile")}
                          onBlur={() => setFocusedField(null)}
                          maxLength="10"
                        />
                      </div>
                      {errors.mobile && (
                        <div className="error-message">
                          <i className="fas fa-exclamation-circle"></i>
                          {errors.mobile}
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Gender *</label>
                      <div className="gender-options">
                        {["Male", "Female", "Other"].map((option) => (
                          <button
                            key={option}
                            type="button"
                            className={`gender-option ${gender === option ? "selected" : ""}`}
                            onClick={() => handleGenderChange(option)}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                      {errors.gender && (
                        <div className="error-message">
                          <i className="fas fa-exclamation-circle"></i>
                          {errors.gender}
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Aadhaar Number *</label>
                      <div
                        className={`input-wrapper ${focusedField === "aadhaar" ? "focused" : ""} ${errors.aadhaar_number ? "error" : ""}`}
                      >
                        <i className="fas fa-address-card input-icon"></i>
                        <input
                          type="text"
                          placeholder="eg. 234567891234"
                          value={dsaForm.aadhaar_number}
                          onChange={(e) =>
                            handleDsaInputChange(
                              "aadhaar_number",
                              e.target.value.replace(/\D/g, ""),
                            )
                          }
                          onFocus={() => setFocusedField("aadhaar")}
                          onBlur={() => setFocusedField(null)}
                          maxLength="12"
                        />
                      </div>
                      {errors.aadhaar_number && (
                        <div className="error-message">
                          <i className="fas fa-exclamation-circle"></i>
                          {errors.aadhaar_number}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Residence Address *</label>
                    <div
                      className={`input-wrapper ${focusedField === "address" ? "focused" : ""} ${errors.address ? "error" : ""}`}
                    >
                      <i className="fas fa-home input-icon"></i>
                      <textarea
                        placeholder="eg. 123 Main Street, Sector 5, Near City Mall"
                        value={dsaForm.address}
                        onChange={(e) =>
                          handleDsaInputChange("address", e.target.value)
                        }
                        onFocus={() => setFocusedField("address")}
                        onBlur={() => setFocusedField(null)}
                        rows="3"
                      />
                    </div>
                    {errors.address && (
                      <div className="error-message">
                        <i className="fas fa-exclamation-circle"></i>
                        {errors.address}
                      </div>
                    )}
                  </div>

                  <div className="form-grid">
                    <div className="form-group">
                      <label>Pincode *</label>
                      <div
                        className={`input-wrapper ${focusedField === "pincode" ? "focused" : ""} ${errors.pincode ? "error" : ""}`}
                      >
                        <i className="fas fa-map-pin input-icon"></i>
                        <input
                          type="text"
                          placeholder="eg. 380015"
                          value={dsaForm.pincode}
                          onChange={handlePincodeChange}
                          onFocus={() => setFocusedField("pincode")}
                          onBlur={(e) => {
                            setFocusedField(null);
                            handlePincodeBlur(e);
                          }}
                          maxLength="6"
                        />
                        {isLoadingLocation && (
                          <span className="loading-spinner">
                            <i className="fas fa-spinner fa-spin"></i>
                          </span>
                        )}
                      </div>
                      {errors.pincode && (
                        <div className="error-message">
                          <i className="fas fa-exclamation-circle"></i>
                          {errors.pincode}
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label>City</label>
                      <div
                        className={`input-wrapper ${focusedField === "city" ? "focused" : ""}`}
                      >
                        <i className="fas fa-city input-icon"></i>
                        <input
                          type="text"
                          placeholder="eg. Ahmedabad"
                          value={dsaForm.city}
                          onChange={(e) =>
                            handleDsaInputChange("city", e.target.value)
                          }
                          onFocus={() => setFocusedField("city")}
                          onBlur={() => setFocusedField(null)}
                          readOnly={!!dsaForm.pincode}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Area / Locality</label>
                      <div
                        className={`input-wrapper ${focusedField === "area" ? "focused" : ""}`}
                      >
                        <i className="fas fa-location-dot input-icon"></i>
                        <input
                          type="text"
                          placeholder="eg. Satellite Road"
                          value={dsaForm.area}
                          onChange={(e) =>
                            handleDsaInputChange("area", e.target.value)
                          }
                          onFocus={() => setFocusedField("area")}
                          onBlur={() => setFocusedField(null)}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>State</label>
                      <div
                        className={`input-wrapper ${focusedField === "state" ? "focused" : ""}`}
                      >
                        <i className="fas fa-flag input-icon"></i>
                        <input
                          type="text"
                          placeholder="eg. Gujarat"
                          value={dsaForm.state}
                          onChange={(e) =>
                            handleDsaInputChange("state", e.target.value)
                          }
                          onFocus={() => setFocusedField("state")}
                          onBlur={() => setFocusedField(null)}
                          readOnly={!!dsaForm.pincode}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={goToNextStep}
                    >
                      Next Step
                      <i className="fas fa-arrow-right"></i>
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Professional Information */}
              {dsaStep === 2 && (
                <div className="form-step">
                  <div className="form-section-header">
                    <h3>
                      <i className="fas fa-briefcase"></i>
                      Professional Information
                    </h3>
                    <p>Tell us about your professional background</p>
                  </div>

                  <div className="form-grid">
                    <div className="form-group">
                      <label>Experience *</label>
                      <div
                        className={`input-wrapper ${focusedField === "experience" ? "focused" : ""} ${errors.experience ? "error" : ""}`}
                      >
                        <i className="fas fa-chart-line input-icon"></i>
                        <select
                          value={dsaForm.experience}
                          onChange={(e) =>
                            handleDsaInputChange("experience", e.target.value)
                          }
                          onFocus={() => setFocusedField("experience")}
                          onBlur={() => setFocusedField(null)}
                        >
                          <option value="">Select Experience</option>
                          <option value="0-1">0-1 years (Fresher)</option>
                          <option value="1-3">1-3 years</option>
                          <option value="3-5">3-5 years</option>
                          <option value="5+">5+ years (Experienced)</option>
                        </select>
                      </div>
                      {errors.experience && (
                        <div className="error-message">
                          <i className="fas fa-exclamation-circle"></i>
                          {errors.experience}
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Current Profession *</label>
                      <div
                        className={`input-wrapper ${focusedField === "profession" ? "focused" : ""} ${errors.profession ? "error" : ""}`}
                      >
                        <i className="fas fa-user-tie input-icon"></i>
                        <select
                          value={dsaForm.profession}
                          onChange={(e) =>
                            handleDsaInputChange("profession", e.target.value)
                          }
                          onFocus={() => setFocusedField("profession")}
                          onBlur={() => setFocusedField(null)}
                        >
                          <option value="">Select Profession</option>
                          <option value="banking">Banking Professional</option>
                          <option value="insurance">Insurance Agent</option>
                          <option value="real_estate">Real Estate Agent</option>
                          <option value="ca">Chartered Accountant</option>
                          <option value="business">Business Owner</option>
                          <option value="sales">Sales Professional</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      {errors.profession && (
                        <div className="error-message">
                          <i className="fas fa-exclamation-circle"></i>
                          {errors.profession}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-grid">
                    {/* Selfie with camera & gallery + hint */}
                    <div className="form-group">
                      <label>Selfie Photo *</label>
                      <input
                        type="file"
                        accept="image/*"
                        capture="user"
                        onChange={(e) =>
                          handleFileUpload(
                            setSelfiePhoto,
                            e.target.files[0],
                            5,
                            true,
                          )
                        }
                        id="selfieCamera"
                        style={{ display: "none" }}
                      />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          handleFileUpload(
                            setSelfiePhoto,
                            e.target.files[0],
                            5,
                            true,
                          )
                        }
                        id="selfieGallery"
                        style={{ display: "none" }}
                      />
                      {!selfiePhoto ? (
                        <div>
                          <div className="dual-button-group">
                            <button
                              type="button"
                              className="btn-file"
                              onClick={() => triggerFileInput("selfieCamera")}
                            >
                              <i className="fas fa-camera"></i> Take Photo
                            </button>
                            <button
                              type="button"
                              className="btn-file"
                              onClick={() => triggerFileInput("selfieGallery")}
                            >
                              <i className="fas fa-folder-open"></i> Choose from
                              Gallery
                            </button>
                          </div>
                          <p className="camera-hint">
                            <i className="fas fa-info-circle"></i> Camera works
                            only on HTTPS. If it doesn't open, use gallery.
                          </p>
                        </div>
                      ) : (
                        <div className="file-preview">
                          {selfiePreviewUrl ? (
                            <img src={selfiePreviewUrl} alt="Selfie preview" />
                          ) : (
                            <i
                              className="fas fa-image"
                              style={{ fontSize: 40, color: "#a0aec0" }}
                            ></i>
                          )}
                          <div className="file-info">
                            <div className="name">{selfiePhoto.name}</div>
                            <div className="size">
                              {formatFileSize(selfiePhoto.size)}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="remove-file"
                            onClick={() => {
                              setSelfiePhoto(null);
                              setSelfiePreviewUrl("");
                            }}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      )}
                      {errors.selfie && (
                        <div className="error-message">
                          <i className="fas fa-exclamation-circle"></i>
                          {errors.selfie}
                        </div>
                      )}
                    </div>

                    {/* Office photo with camera & gallery + hint */}
                    <div className="form-group">
                      <label>Office/Workspace Photo (Optional)</label>
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) =>
                          handleFileUpload(
                            setOfficePhoto,
                            e.target.files[0],
                            5,
                            true,
                          )
                        }
                        id="officeCamera"
                        style={{ display: "none" }}
                      />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          handleFileUpload(
                            setOfficePhoto,
                            e.target.files[0],
                            5,
                            true,
                          )
                        }
                        id="officeGallery"
                        style={{ display: "none" }}
                      />
                      {!officePhoto ? (
                        <div>
                          <div className="dual-button-group">
                            <button
                              type="button"
                              className="btn-file"
                              onClick={() => triggerFileInput("officeCamera")}
                            >
                              <i className="fas fa-camera"></i> Take Photo
                            </button>
                            <button
                              type="button"
                              className="btn-file"
                              onClick={() => triggerFileInput("officeGallery")}
                            >
                              <i className="fas fa-folder-open"></i> Choose from
                              Gallery
                            </button>
                          </div>
                          <p className="camera-hint">
                            <i className="fas fa-info-circle"></i> Camera works
                            only on HTTPS. If it doesn't open, use gallery.
                          </p>
                        </div>
                      ) : (
                        <div className="file-preview">
                          {officePreviewUrl ? (
                            <img src={officePreviewUrl} alt="Office preview" />
                          ) : (
                            <i
                              className="fas fa-image"
                              style={{ fontSize: 40, color: "#a0aec0" }}
                            ></i>
                          )}
                          <div className="file-info">
                            <div className="name">{officePhoto.name}</div>
                            <div className="size">
                              {formatFileSize(officePhoto.size)}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="remove-file"
                            onClick={() => {
                              setOfficePhoto(null);
                              setOfficePreviewUrl("");
                            }}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={goToPrevStep}
                    >
                      <i className="fas fa-arrow-left"></i>
                      Previous
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={goToNextStep}
                    >
                      Next Step
                      <i className="fas fa-arrow-right"></i>
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Documents */}
              {dsaStep === 3 && (
                <div className="form-step">
                  <div className="form-section-header">
                    <h3>
                      <i className="fas fa-file-alt"></i>
                      Required Documents
                    </h3>
                    <p>Upload necessary documents for verification</p>
                  </div>

                  <div className="form-grid">
                    {/* Aadhaar Front */}
                    <div className="form-group">
                      <label>Aadhaar Front *</label>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) =>
                          handleFileUpload(setAadhaarFront, e.target.files[0])
                        }
                        id="aadhaarFront"
                        style={{ display: "none" }}
                      />
                      {!aadhaarFront ? (
                        <div
                          className="file-upload-area"
                          onClick={() =>
                            document.getElementById("aadhaarFront").click()
                          }
                        >
                          <i className="fas fa-id-card"></i>
                          <p>Upload Aadhaar Front</p>
                          <small>JPG, PNG or PDF up to 5MB</small>
                        </div>
                      ) : (
                        <div className="file-preview">
                          {aadhaarFront.type.startsWith("image/") ? (
                            <img
                              src={URL.createObjectURL(aadhaarFront)}
                              alt="Aadhaar Front"
                              style={{
                                width: 60,
                                height: 60,
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <i
                              className="fas fa-file-pdf"
                              style={{ fontSize: 40, color: "#f56565" }}
                            ></i>
                          )}
                          <div className="file-info">
                            <div className="name">{aadhaarFront.name}</div>
                            <div className="size">
                              {formatFileSize(aadhaarFront.size)}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="remove-file"
                            onClick={() => setAadhaarFront(null)}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      )}
                      {errors.aadhaarFront && (
                        <div className="error-message">
                          <i className="fas fa-exclamation-circle"></i>
                          {errors.aadhaarFront}
                        </div>
                      )}
                    </div>

                    {/* Aadhaar Back */}
                    <div className="form-group">
                      <label>Aadhaar Back *</label>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) =>
                          handleFileUpload(setAadhaarBack, e.target.files[0])
                        }
                        id="aadhaarBack"
                        style={{ display: "none" }}
                      />
                      {!aadhaarBack ? (
                        <div
                          className="file-upload-area"
                          onClick={() =>
                            document.getElementById("aadhaarBack").click()
                          }
                        >
                          <i className="fas fa-id-card"></i>
                          <p>Upload Aadhaar Back</p>
                          <small>JPG, PNG or PDF up to 5MB</small>
                        </div>
                      ) : (
                        <div className="file-preview">
                          {aadhaarBack.type.startsWith("image/") ? (
                            <img
                              src={URL.createObjectURL(aadhaarBack)}
                              alt="Aadhaar Back"
                              style={{
                                width: 60,
                                height: 60,
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <i
                              className="fas fa-file-pdf"
                              style={{ fontSize: 40, color: "#f56565" }}
                            ></i>
                          )}
                          <div className="file-info">
                            <div className="name">{aadhaarBack.name}</div>
                            <div className="size">
                              {formatFileSize(aadhaarBack.size)}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="remove-file"
                            onClick={() => setAadhaarBack(null)}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      )}
                      {errors.aadhaarBack && (
                        <div className="error-message">
                          <i className="fas fa-exclamation-circle"></i>
                          {errors.aadhaarBack}
                        </div>
                      )}
                    </div>

                    {/* PAN Card */}
                    <div className="form-group">
                      <label>PAN Card *</label>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) =>
                          handleFileUpload(setPanCardPhoto, e.target.files[0])
                        }
                        id="panCard"
                        style={{ display: "none" }}
                      />
                      {!panCardPhoto ? (
                        <div
                          className="file-upload-area"
                          onClick={() =>
                            document.getElementById("panCard").click()
                          }
                        >
                          <i className="fas fa-id-card"></i>
                          <p>Upload PAN Card</p>
                          <small>JPG, PNG or PDF up to 5MB</small>
                        </div>
                      ) : (
                        <div className="file-preview">
                          {panCardPhoto.type.startsWith("image/") ? (
                            <img
                              src={URL.createObjectURL(panCardPhoto)}
                              alt="PAN Card"
                              style={{
                                width: 60,
                                height: 60,
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <i
                              className="fas fa-file-pdf"
                              style={{ fontSize: 40, color: "#f56565" }}
                            ></i>
                          )}
                          <div className="file-info">
                            <div className="name">{panCardPhoto.name}</div>
                            <div className="size">
                              {formatFileSize(panCardPhoto.size)}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="remove-file"
                            onClick={() => setPanCardPhoto(null)}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      )}
                      {errors.panCard && (
                        <div className="error-message">
                          <i className="fas fa-exclamation-circle"></i>
                          {errors.panCard}
                        </div>
                      )}
                    </div>

                    {/* Cancel Cheque */}
                    <div className="form-group">
                      <label>Cancel Cheque *</label>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) =>
                          handleFileUpload(setCancelCheque, e.target.files[0])
                        }
                        id="cancelCheque"
                        style={{ display: "none" }}
                      />
                      {!cancelCheque ? (
                        <div
                          className="file-upload-area"
                          onClick={() =>
                            document.getElementById("cancelCheque").click()
                          }
                        >
                          <i className="fas fa-money-check"></i>
                          <p>Upload Cancel Cheque</p>
                          <small>JPG, PNG or PDF up to 5MB</small>
                        </div>
                      ) : (
                        <div className="file-preview">
                          {cancelCheque.type.startsWith("image/") ? (
                            <img
                              src={URL.createObjectURL(cancelCheque)}
                              alt="Cancel Cheque"
                              style={{
                                width: 60,
                                height: 60,
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <i
                              className="fas fa-file-pdf"
                              style={{ fontSize: 40, color: "#f56565" }}
                            ></i>
                          )}
                          <div className="file-info">
                            <div className="name">{cancelCheque.name}</div>
                            <div className="size">
                              {formatFileSize(cancelCheque.size)}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="remove-file"
                            onClick={() => setCancelCheque(null)}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      )}
                      {errors.cancelCheque && (
                        <div className="error-message">
                          <i className="fas fa-exclamation-circle"></i>
                          {errors.cancelCheque}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Other Documents */}
                  <div
                    className="form-section-header"
                    style={{ marginTop: 30 }}
                  >
                    <h4>
                      <i className="fas fa-file-upload"></i>
                      Other Documents (Optional)
                    </h4>
                  </div>

                  {otherDocuments.map((doc) => (
                    <div key={doc.id} className="other-document-item">
                      <div className="other-document-header">
                        <input
                          type="text"
                          placeholder="Document description"
                          value={doc.text}
                          onChange={(e) => {
                            const updated = otherDocuments.map((d) =>
                              d.id === doc.id
                                ? { ...d, text: e.target.value }
                                : d,
                            );
                            setOtherDocuments(updated);
                          }}
                        />
                        <button
                          type="button"
                          className="remove-doc-btn"
                          onClick={() => removeOtherDocumentField(doc.id)}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>

                      <input
                        type="file"
                        accept="image/*,.pdf,.doc,.docx"
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            const file = e.target.files[0];
                            const updated = otherDocuments.map((d) =>
                              d.id === doc.id
                                ? {
                                    ...d,
                                    file: file,
                                    name: file.name,
                                    size: file.size,
                                    type: file.type,
                                  }
                                : d,
                            );
                            setOtherDocuments(updated);
                          }
                        }}
                        id={`doc-${doc.id}`}
                        style={{ display: "none" }}
                      />

                      {!doc.file ? (
                        <div
                          className="file-upload-area"
                          style={{ padding: 20 }}
                          onClick={() =>
                            document.getElementById(`doc-${doc.id}`).click()
                          }
                        >
                          <i className="fas fa-upload"></i>
                          <p>Choose file</p>
                        </div>
                      ) : (
                        <div className="file-preview">
                          {doc.file.type.startsWith("image/") ? (
                            <img
                              src={URL.createObjectURL(doc.file)}
                              alt="Document"
                              style={{
                                width: 60,
                                height: 60,
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <i
                              className="fas fa-file"
                              style={{ fontSize: 30, color: "#667eea" }}
                            ></i>
                          )}
                          <div className="file-info">
                            <div className="name">{doc.name}</div>
                            <div className="size">
                              {formatFileSize(doc.size)}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="remove-file"
                            onClick={() => {
                              const updated = otherDocuments.map((d) =>
                                d.id === doc.id
                                  ? { ...d, file: null, name: "", size: 0 }
                                  : d,
                              );
                              setOtherDocuments(updated);
                            }}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  ))}

                  <button
                    type="button"
                    className="add-more-btn"
                    onClick={addOtherDocumentField}
                  >
                    <i className="fas fa-plus"></i>
                    Add More Documents
                  </button>

                  {/* Terms and Conditions */}
                  <div className="terms-section">
                    <h4 style={{ marginBottom: 15 }}>
                      <i className="fas fa-file-contract"></i>
                      Terms & Conditions
                    </h4>
                    <div
                      style={{
                        marginBottom: 20,
                        color: "#4a5568",
                        fontSize: 14,
                      }}
                    >
                      <p>By submitting this form, you agree to:</p>
                      <ul style={{ marginTop: 10, paddingLeft: 20 }}>
                        <li>
                          All information provided is accurate and complete
                        </li>
                        <li>Undergo KYC verification process</li>
                        <li>Receive commission as per company policy</li>
                        <li>
                          Maintain confidentiality of customer information
                        </li>
                        <li>Comply with all regulatory requirements</li>
                      </ul>
                    </div>

                    <div className="checkbox-wrapper">
                      <input
                        type="checkbox"
                        id="agreeTerms"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                      />
                      <label htmlFor="agreeTerms">
                        I have read and agree to the{" "}
                        <a href="#">Terms & Conditions</a> and{" "}
                        <a href="#">Privacy Policy</a> *
                      </label>
                    </div>
                    {errors.terms && (
                      <div className="error-message" style={{ marginTop: 10 }}>
                        <i className="fas fa-exclamation-circle"></i>
                        {errors.terms}
                      </div>
                    )}
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={goToPrevStep}
                    >
                      <i className="fas fa-arrow-left"></i>
                      Previous
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane"></i>
                          Submit Application
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Support Note */}
              <div
                style={{
                  marginTop: 30,
                  padding: 20,
                  background: "#f0f4ff",
                  borderRadius: 12,
                  display: "flex",
                  alignItems: "center",
                  gap: 15,
                }}
              >
                <i
                  className="fas fa-headset"
                  style={{ fontSize: 30, color: "#667eea" }}
                ></i>
                <div>
                  <p
                    style={{
                      fontWeight: 600,
                      color: "#2d3748",
                      marginBottom: 5,
                    }}
                  >
                    Need Help?
                  </p>
                  <p style={{ color: "#4a5568", fontSize: 14 }}>
                    Our support team will contact you within 24 hours
                  </p>
                  <p style={{ color: "#667eea", fontSize: 13, marginTop: 5 }}>
                    <i className="fas fa-phone"></i> 9909002220 |
                    <i
                      className="fas fa-envelope"
                      style={{ marginLeft: 10 }}
                    ></i>{" "}
                    partner@krishnaloan.com
                  </p>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
